import { useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import NotesWorkspace from 'components/NotesWorkspace'
import type { NoteImageRecord, NotesSurfaceAdapter } from 'components/NotesWorkspace'
import { createNote, deleteNote, initializeNotes, updateNote } from 'slices/locationNoteSlice'
import { createImage, getImages, updateImage, deleteImage } from 'slices/locationImageSlice'
import { patchLocation } from 'slices/locationSlice'
import { addUpdateNoteToProject, initializeProjects, patchProject } from 'slices/projectSlice'
import { prepareNoteHtml } from 'utils/noteUpload'
import { removeImageFromNoteHtml } from 'utils/noteImages'
import { isAskMdtAvailable } from 'features/AskMdt/availability'

/**
 * The location note and image slices predate the typed-slice convention: their
 * payload creators declare no parameter type, so TypeScript infers zero-argument
 * action creators and rejects the payloads they in fact require.
 *
 * Widened here rather than in the slices, which other screens share.
 */
const createNoteThunk = createNote as any
const createImageThunk = createImage as any

interface LocationNotesProps {
	/** Defaults to the :id route param. Projects pass their asset's id. */
	locationid?: string
	projectid?: string | null
	/** Overridden by the Project tab so the subtitle names the project. */
	subtitle?: string
	surfaceKey?: 'location' | 'project'
}

/**
 * Notes for a location, in the two-column workspace layout.
 *
 * Also serves the Project tab: project notes are LocationNotes scoped by
 * `projectid`, which is why ProjectNotes delegates here.
 */
const LocationNotes = ({
	locationid,
	projectid,
	subtitle,
	surfaceKey = 'location',
}: LocationNotesProps) => {
	const dispatch = useDispatch()
	const { id: routeId, projectId: routeProjectId } = useParams<{
		id: string
		projectId: string
	}>()

	const locationId = locationid || (routeId as string)
	const effectiveProjectId = routeProjectId || projectid || null

	const userInfo = useSelector((state: any) => state.userLogin.userInfo)
	const userId = userInfo?._id
	// "Make tasks" from a note (MYD-643), where Ask MDT is offered.
	const canMakeTasks = isAskMdtAvailable(userInfo)

	const location = useSelector((state: any) => state.locations.entities[locationId])
	// Populated as an object by every locations query; tolerate a bare id too, so
	// a leaner payload later does not silently disable the delete control.
	const ownerId = location?.owner?._id ?? location?.owner

	// This tab shows the location's own notes AND its projects' notes in one
	// feed, because the endpoint returns every note for the location when no
	// project is named. The project list is what turns a bare projectid on those
	// notes into something a reader can act on.
	const { entities: projectEntities, ids: projectIds } = useSelector(
		(state: any) => state.projects,
	)

	const {
		entities: noteEntities,
		ids: noteIds,
		isLoading,
		error,
	} = useSelector((state: any) => state.locationNotes)

	const { entities: imageEntities, ids: imageIds } = useSelector(
		(state: any) => state.locationImages,
	)

	useEffect(() => {
		dispatch(initializeNotes({ locationId, projectId: effectiveProjectId }) as any)
		// The gallery rows back the review dialog inside each note: the note HTML
		// carries only URLs, so the rows are what make an image editable.
		dispatch(
			getImages({
				locationid: locationId,
				projectid: effectiveProjectId,
				userid: userId,
			}) as any,
		)
	}, [dispatch, locationId, effectiveProjectId, userId])

	// Only the location-wide view mixes projects together, so only it needs the
	// project names.
	useEffect(() => {
		if (!effectiveProjectId) dispatch(initializeProjects({ locationId }) as any)
	}, [dispatch, locationId, effectiveProjectId])

	const projectNameById = useMemo<Record<string, string>>(() => {
		const out: Record<string, string> = {}
		for (const pid of projectIds || []) {
			const project = projectEntities[pid]
			if (project?._id) out[String(project._id)] = project.projectName || 'Project'
		}
		return out
	}, [projectIds, projectEntities])

	const notes = useMemo(
		() => noteIds.map((id: string) => noteEntities[id]).filter(Boolean),
		[noteIds, noteEntities],
	)

	const imageRecords = useMemo<NoteImageRecord[]>(
		() => imageIds.map((id: string) => imageEntities[id]).filter(Boolean),
		[imageIds, imageEntities],
	)

	const refetch = useMemo(
		() => async () => {
			await dispatch(initializeNotes({ locationId, projectId: effectiveProjectId }) as any)
			await dispatch(
				getImages({
					locationid: locationId,
					projectid: effectiveProjectId,
					userid: userId,
				}) as any,
			)
		},
		[dispatch, locationId, effectiveProjectId, userId],
	)

	const adapter = useMemo<NotesSurfaceAdapter>(
		() => ({
			key: surfaceKey,
			title: 'Notes',
			subtitle: subtitle || location?.assetName || 'this location',
			supportsReplies: true,
			notes,
			imageRecords,
			isLoading,
			error,
			currentUserId: userId,

			/**
			 * Label a note that belongs to one of this location's projects.
			 *
			 * The notes endpoint returns every note for a location when no project
			 * is named, so this tab shows the location's own notes and its
			 * projects' notes together. Without the label they are
			 * indistinguishable, and a reader cannot tell which work a note is
			 * about.
			 *
			 * Nothing is labelled on a project's own tab: every note there belongs
			 * to that project, so the tag would be noise on every card.
			 */
			tagForNote: note => {
				if (effectiveProjectId) return null
				// `projectid` is not populated by the endpoint, so it is an id.
				const projectId = note.projectid?._id ?? note.projectid
				if (!projectId) return null
				const name = projectNameById[String(projectId)]
				return name ? { label: name, title: `From project: ${name}` } : null
			},

			createNote: async ({ contents, parentid, libraryImages }) => {
				// The composer hands back one body per note: several when the
				// author chose a separate note per image, one otherwise.
				for (const [position, body] of contents.entries()) {
					const { html, uploaded } = await prepareNoteHtml(body, effectiveProjectId || '', {
						// Library rows belong to the submission, not to each body,
						// so only the first pass reports them. Otherwise N notes
						// would write N copies of the same gallery row.
						libraryImages: position === 0 ? libraryImages : [],
					})

					await dispatch(
						createNoteThunk({
							userid: userId,
							locationid: locationId,
							projectid: effectiveProjectId,
							note: html,
							parentid: parentid || null,
						}) as any,
					)

					for (const image of uploaded) {
						await dispatch(
							createImageThunk({
								userid: userId,
								locationid: locationId,
								projectid: effectiveProjectId,
								uuid: image.public_id,
								name: image.original_filename,
								size: image.bytes,
								url: image.secure_url,
								type: image.format,
							}) as any,
						)
					}
				}

				// Keeps the "last update" line on the parent records in step with
				// the feed.
				const summary = `New note created by ${userInfo?.name}.`
				if (effectiveProjectId) {
					dispatch(
						addUpdateNoteToProject({
							projectId: effectiveProjectId,
							updateNote: summary,
						}) as any,
					)
					dispatch(
						patchProject({
							projectId: effectiveProjectId,
							updates: { updateNote: summary },
						}) as any,
					)
				}
				dispatch(patchLocation({ locationId, updates: { updateNote: summary } }) as any)

				await refetch()
			},

			updateImage: async image => {
				await dispatch(updateImage(image) as any)
				await dispatch(
					getImages({
						locationid: locationId,
						projectid: effectiveProjectId,
						userid: userId,
					}) as any,
				)
			},

			/**
			 * Removing an image takes it out of both places it lives: the note's
			 * HTML and the gallery. Dropping only the row would leave the note
			 * rendering a picture that no longer exists.
			 */
			removeImage: async ({ image, url, noteId, noteBody }) => {
				const nextHtml = removeImageFromNoteHtml(noteBody, url)
				if (noteId && nextHtml !== noteBody) {
					await dispatch(updateNote({ _id: noteId, note: nextHtml }) as any)
				}
				await dispatch(deleteImage({ imageid: image._id }) as any)
				await refetch()
			},

			/**
			 * Show-only. The server decides for real in `noteDeleteGuards`; this
			 * matches its "who" rule so the control is not offered for something
			 * that would come back 403.
			 *
			 * A partner with write access deliberately gets no control: write is
			 * permission to contribute, not to erase.
			 *
			 * `ownerId` can be undefined on the Project tab. That tab reaches this
			 * screen through `ProjectNotes`, which resolves the location id from
			 * `project.projectAsset._id` — but `projectController` populates
			 * `projectAsset` without `owner`, so the owner is only known when the
			 * location itself happens to be in `state.locations`. When it is not,
			 * this falls back to author-only, which under-offers rather than
			 * offering a button that fails.
			 */
			canDeleteNote: note => {
				if (!userId) return false
				const authorId = note.userid?._id ?? note.userid
				if (authorId && String(authorId) === String(userId)) return true
				return Boolean(ownerId && String(ownerId) === String(userId))
			},

			deleteNote: async note => {
				const action: any = await dispatch(deleteNote(note._id) as any)
				if (action?.meta?.requestStatus === 'rejected') {
					// A rejected thunk resolves rather than throwing, so this check is
					// what turns the server's refusal into something the dialog shows.
					return String(action.payload ?? 'Could not delete this note.')
				}
				// The slice already dropped the note from the store. The refetch is
				// for the gallery rows the server soft-deleted alongside it, which
				// would otherwise keep showing in the rail until the tab remounts.
				await refetch()
				return null
			},

			canMakeTasks,
		}),
		[
			dispatch,
			surfaceKey,
			subtitle,
			location?.assetName,
			ownerId,
			notes,
			imageRecords,
			isLoading,
			error,
			userId,
			userInfo?.name,
			locationId,
			effectiveProjectId,
			projectNameById,
			refetch,
			canMakeTasks,
		],
	)

	return <NotesWorkspace adapter={adapter} />
}

export default LocationNotes
